import { isObject } from '@/global/utils/utils'
import indexTableLeafletMapHelper from '../../../common/config/index-table-leaflet-map-helper'
import { dateService, FORMATS } from '@/global/services/date'
import truncatedTextDisplay from '@/global/components/TruncatedTextDisplay.vue'
import { notify } from '@/global/services/notification/notificationBus'
import { showDetailsAction } from '@/global/components/table/helpers/tableActions'
import { STATUS_PILL_CELL_CONFIG } from '../../../common/config/status-cell-helper'

export default {
  name: 'mainTableConfig',

  mixins: [
    indexTableLeafletMapHelper
  ],

  computed: {
    tableConfig () {
      return {
        tableConfig: {
          uiView: 'eRegulation_requests_index',
          callFetchData: this.refreshTable,
          apiConfig: {
            get: {
              module: 'road-maintenance',
              route: 'traffic-regulation',
              queryParams: (event) => this.modifyFilters(event)
            }
          },
          showLoader: this.showLoader,
          // Opening the record is the double click, everywhere in the application.
          rowDblClick: (event) => this.showDetails(event),
          // A click on a row shows it on the map beside the table
          rowClickIndicator: 'map',
          rowsSelection: {
            // Picking rows is what tells the map what to show: their markers are
            // marked and the map is moved to hold all of them at once.
            selectionsChange: (event) => this.showSelectedMarkers(event)
          },
          onDataReceivedWithParams: (event) => {
            this.refreshTable = false
            this.fetchMarkers(event).then()
          },
          filters_and_columns_visibility_config: {
            showNewBtn: !!this.permissions?.canCreate,
            newBtnPosition: 'start',
            newClick: () => this.onNewClick()
          },
          sideTableComponent: {
            component: this.mainTableLeafletMapComponent,
            mapInitialized: async (event) => await this.mapInitialized(event),
            onAddedMarkers: () => {
              this.showLoader = false
            },
            queryParams: {
              type: 'REQUEST'
            }
          },
          globalStyle: {
            tableRowClasses: 'table-row',
            sideTableContentColClasses: 'table-side-col'
          },
          tableConfig: {
            globalConfig: {
              rowStyle: (event) => this.highlightSameRows(event)
            },
            cellsConfig: {
              source: {
                custom_display_value: (data) => {
                  return data?.source ? this.$t(data.source) : ''
                }
              },
              request_type: {
                custom_display_value: (data) => {
                  return data?.request_type?.description ? this.$t(data.request_type.description) : ''
                }
              },
              processing_entity: {
                custom_display_value: (data) => {
                  return data?.processing_entity ? this.$t(data.processing_entity) : ''
                }
              },
              status: {
                type: 'status',
                custom_display_value: (data) => {
                  if (data && data.status && isObject(data.status)) {
                    return {
                      ...data.status,
                      ...{
                        description: data.status.description ? this.$t(data.status.description) : '-'
                      }
                    }
                  }
                },
                displayValue: 'description',
                colorObjectKey: 'color',
                textColorKey: 'text',
                backgroundColorKey: 'background',
                ...STATUS_PILL_CELL_CONFIG
              }
            },
            headers: [
              {
                label: this.$t('road-maintenance/aggregator.request_id'),
                key: 'document_code',
                always_visible: true
              },
              {
                label: this.$t('road-maintenance/aggregator.request_created_date'),
                key: 'created_at',
                always_visible: true
              },
              {
                label: this.$t('road-maintenance/dropdown-types.applicant'),
                key: 'applicant',
                component: truncatedTextDisplay,
                initial_value: true
              },
              {
                label: this.$t('road-maintenance/aggregator.type_of_request'),
                key: 'request_type',
                initial_value: true
              },
              {
                label: this.$t('fleet/vehicle.status'),
                key: 'status',
                style: {
                  width: '120px'
                },
                always_visible: true
              },
              {
                label: this.$t('road-maintenance/aggregator.street'),
                key: 'address',
                component: truncatedTextDisplay,
                initial_value: true
              },
              {
                label: this.$t('road-maintenance/aggregator.responsible_for_processing'),
                key: 'processing_entity',
                initial_value: true
              },
              {
                label: this.$t('road-maintenance/aggregator.delegated'),
                key: 'employee',
                initial_value: true
              },
              {
                label: this.$t('road-maintenance/aggregator.estimated_start_time'),
                key: 'started_at',
                initial_value: true
              },
              {
                label: this.$t('road-maintenance/aggregator.estimated_completion_time'),
                key: 'completed_at',
                initial_value: true
              },
              {
                label: this.$t('road-maintenance/aggregator.registration_number'),
                key: 'registry_number',
                initial_value: true
              },
              {
                label: this.$t('road-maintenance/aggregator.class'),
                key: 'doc_class',
                initial_value: true
              },
              {
                label: this.$t('road-maintenance/aggregator.source'),
                key: 'source',
                initial_value: true
              }
            ],
            paginationConfig: {
              perPageOptions: [20, 50, 100],
              perPage: 100,
              currentPage: 1
            },
            actionsConfig: [
              showDetailsAction(this.$t, (event) => this.showDetails(event))
            ],
            filtersConfig: {
              fields: [
                {
                  key: 'date_time_from',
                  label: this.$t('road-maintenance/aggregator.date_from'),
                  type: 'datetime-picker',
                  appendIcon: 'mdi-calendar-month',
                  format: FORMATS.datetimeShort,
                  placeholder: FORMATS.datetimeShort
                },
                {
                  key: 'date_time_to',
                  label: this.$t('road-maintenance/aggregator.date_to'),
                  type: 'datetime-picker',
                  appendIcon: 'mdi-calendar-month',
                  format: FORMATS.datetimeShort,
                  placeholder: FORMATS.datetimeShort
                },
                {
                  key: 'document_code',
                  label: this.$t('road-maintenance/aggregator.request_id')
                },
                {
                  key: 'request_type_id',
                  label: this.$t('road-maintenance/aggregator.type_of_request'),
                  type: 'autocomplete',
                  clearable: true,
                  items: this.dropdownValues?.request_type || [],
                  autocomplete_options: {
                    custom_display_value: (value) => {
                      const { description } = value || null
                      return description && typeof description === 'string' ? this.$t(description) : ''
                    },
                    form_value: 'id'
                  }
                },
                {
                  key: 'status_id',
                  label: this.$t('fleet/vehicle.status'),
                  type: 'autocomplete',
                  clearable: true,
                  autocomplete_options: {
                    module: 'road-maintenance',
                    route: 'traffic-regulation/statuses',
                    allowSearch: false,
                    custom_display_value: (value) => {
                      const { description } = value || null
                      return description && typeof description === 'string' ? this.$t(description) : ''
                    },
                    form_value: 'id'
                  }
                },
                {
                  key: 'processing_entity_id',
                  label: this.$t('road-maintenance/aggregator.responsible_for_processing'),
                  type: 'autocomplete',
                  clearable: true,
                  items: this.dropdownValues?.processing_entity || [],
                  autocomplete_options: {
                    custom_display_value: (value) => {
                      const { description } = value || null
                      return description && typeof description === 'string' ? this.$t(description) : ''
                    },
                    form_value: 'id'
                  }
                },
                {
                  key: 'milestone_type_id',
                  label: this.$t('road-maintenance/aggregator.finished_phases'),
                  type: 'autocomplete',
                  clearable: true,
                  items: this.dropdownValues?.traffic_regulation_milestone_type || [],
                  autocomplete_options: {
                    custom_display_value: (value) => {
                      const { description } = value || null
                      return description && typeof description === 'string' ? this.$t(description) : ''
                    },
                    form_value: 'id'
                  }
                },
                {
                  key: 'doc_class',
                  label: this.$t('road-maintenance/aggregator.class')
                },
                {
                  key: 'registry_number',
                  label: this.$t('road-maintenance/aggregator.registration_number')
                },
                {
                  key: 'applicant',
                  label: this.$t('road-maintenance/prp.request_applicant')
                }
              ]
            }
          }
        },
        breadcrumbs: {
          items: [
            {
              text: this.$t('road-maintenance.eRegulation')
            },
            {
              text: this.$t('road-maintenance.requests'),
              bolded: true
            }
          ]
        }
      }
    }
  },

  methods: {
    highlightSameRows ({ rowData } = {}) {
      const { is_highlighted: isHighlighted } = rowData || {}
      return isHighlighted
        ? {
            backgroundColor: '#f5f1f1'
          }
        : {}
    },

    showDetails (event) {
      const { data } = event || {}
      const { id, request_type: requestType, document_code: documentCode } = data || {}
      const query = {
        category: 'request_overview',
        request_type: requestType?.name ?? '',
        document_code: documentCode
      }

      // skip if request id or type is invalid
      if (!id || !requestType) {
        notify(this.$t('road-maintenance/aggregator.request_not_found'))
        return
      }

      this.$router.push({
        name: 'Aggregator_requests_details',
        params: {
          id
        },
        query
      }).then()
    },

    onNewClick () {
      this.showRequestCreationView = true
    },

    onMarkerClick (marker) {
      const { id } = marker || {}

      if (!id) {
        return
      }

      this.$router.replace({
        name: 'Aggregator_requests_details',
        params: {
          id
        }
      })
    },

    modifyFilters (event) {
      const { filters, sort } = event?.params || {}
      const newFilters = {
        filters: { ...filters },
        type: 'REQUEST',
        sort: {
          by: sort?.by || null,
          desc: true
        }
      }

      // format date times
      if (filters?.date_time_from) {
        newFilters.filters.date_time_from = dateService.formatCustom(filters?.date_time_from, FORMATS.datetimeShort, FORMATS.isoDatetime)
      }
      if (filters?.date_time_to) {
        newFilters.filters.date_time_to = dateService.formatCustom(filters?.date_time_to, FORMATS.datetimeShort, FORMATS.isoDatetime)
      }

      return newFilters
    }
  }
}
